import { useState, useEffect, useMemo } from 'react';
import {
  Settings, Bot, Volume2, MessageSquare, FileText, Zap, Phone, Mic,
  Loader2, Play, Check, X, HelpCircle, Plus,
  SlidersHorizontal, ChevronRight, ChevronLeft, GitBranch
} from 'lucide-react';
import { AgentConfigShell, type AgentSectionDef } from '@/components/ai/shell/AgentConfigShell';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { apiClient, type ApiResponse } from '@/lib/api';
import { isTenantLimitHandled } from '@/lib/tenantLimits';
import { useChatModelOptions } from '@/hooks/useChatModelOptions';
import PreCallHooksSection, { type PreCallHooksConfig } from '@/components/ai/PreCallHooksSection';
import VoiceAgentFlowBuilder, { type FlowDefinition } from '@/components/ai/VoiceAgentFlowBuilder';
import { KnowledgeBaseManager } from '@/components/ai/KnowledgeBaseManager';
import {
  KbModelChangeGuardDialog,
  useKbModelChangeGuard,
  type KbGuardedPerformSaveArgs,
} from '@/components/ai/KbModelChangeGuardDialog';
import { getErrorMessage } from '../../lib/utils';


interface AIProvider {
  id: string;
  name: string;
  provider_type: string;
  api_key_hint: string;
  is_active: boolean;
  is_valid: boolean;
  last_tested_at: string | null;
  openai_project_id?: string;
  routing_mode?: 'legacy_ws_bridge' | 'openai_sip' | 'shadow_both';
  webhook_secret_configured?: boolean;
  webhook_url?: string;
  org_key_configured?: boolean;
  org_api_key_hint?: string;
}

interface RealtimeConfig {
  voice?: string;
  speed?: number;
  voice_instructions?: string;
  stability?: number;
  speaking_rate?: number;
  expressiveness?: string;
  handoff_type?: string;
  handoff_destination?: string;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  language?: string;
  vad_type?: 'server_vad' | 'semantic_vad' | 'none';
  vad_threshold?: number;
  vad_prefix_padding_ms?: number;
  vad_silence_duration_ms?: number;
  vad_eagerness?: 'low' | 'medium' | 'high' | 'auto';
  aec_enabled?: boolean;
  // Task #2089 — esposti SOLO per la famiglia `gpt-realtime-2`.
  reasoning_effort?: 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';
  preambles_enabled?: boolean;
}

// Regex per prefisso (mirror di utils/realtime-model.util BE): riconosce la
// famiglia v2 senza liste hardcoded di model id.
const REALTIME2_PREFIX_RE = /^gpt-realtime-(2|translate|whisper)/;

function isRealtime2Model(model: string | undefined | null): boolean {
  if (!model) return false;
  return REALTIME2_PREFIX_RE.test(model.toLowerCase());
}

export interface AIVoiceAgent {
  id: string;
  name: string;
  description: string | null;
  model: string;
  temperature: number;
  max_tokens: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  system_prompt: string;
  provider_id: string | null;
  rag_enabled: boolean;
  rag_inferential_enabled?: boolean;
  rag_synthesis_mode?: string;
  is_active: boolean;
  conversation_states?: Record<string, unknown>[];
  available_tools?: string[];
  handoff_extension?: string;
  language?: string;
  voice_id?: string;
  realtime_config?: RealtimeConfig;
  transcription_enabled?: boolean;
  sentiment_enabled?: boolean;
  context_memory_enabled?: boolean;
  semantic_memory_enabled?: boolean;
  agent_platform?: string;
  pre_call_hooks?: PreCallHooksConfig;
  flow_definition?: FlowDefinition | null;
  reasoning_effort?: 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';
  preambles_enabled?: boolean;
}


interface OpenAIVoiceAgentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: AIVoiceAgent | null;
  onSaved: () => void;
  activeProvider?: AIProvider;
  extensions: { id: string; extension_number: string; display_name: string }[];
  ringGroups: { id: string; name: string; extension: string }[];
}

// Fallback minimale se il catalog è offline: il picker completo arriva dal
// catalog (SoT ai_model_catalog), qui solo i due default sicuri.
const FALLBACK_MODELS = [
  { id: 'gpt-realtime-2', name: 'GPT Realtime 2 (GA - Consigliato)', description: 'Speech-to-speech v2 con reasoning + preambles, voci Cedar/Marin' },
  { id: 'gpt-realtime', name: 'GPT Realtime (GA)', description: 'Modello speech-to-speech GA precedente' },
];

const TTS_VOICES = [
  { id: 'marin', name: 'Marin', description: 'Professionale e naturale (Consigliata)', quality: 'premium' },
  { id: 'cedar', name: 'Cedar', description: 'Calda e professionale', quality: 'premium' },
  { id: 'alloy', name: 'Alloy', description: 'Neutrale e bilanciata', quality: 'standard' },
  { id: 'ash', name: 'Ash', description: 'Calda e naturale', quality: 'standard' },
  { id: 'ballad', name: 'Ballad', description: 'Espressiva e melodica', quality: 'standard' },
  { id: 'coral', name: 'Coral', description: 'Amichevole e chiara', quality: 'standard' },
  { id: 'echo', name: 'Echo', description: 'Profonda e risonante', quality: 'standard' },
  { id: 'fable', name: 'Fable', description: 'Narrativa e coinvolgente', quality: 'standard' },
  { id: 'onyx', name: 'Onyx', description: 'Autoritaria e forte', quality: 'standard' },
  { id: 'nova', name: 'Nova', description: 'Giovane e dinamica', quality: 'standard' },
  { id: 'sage', name: 'Sage', description: 'Saggia e rassicurante', quality: 'standard' },
  { id: 'shimmer', name: 'Shimmer', description: 'Brillante e vivace', quality: 'standard' },
  { id: 'verse', name: 'Verse', description: 'Poetica e fluida', quality: 'standard' }
];

const VOICE_INSTRUCTION_PRESETS = [
  {
    id: 'assistenza_clienti',
    label: 'Assistenza Clienti',
    text: 'Parla con tono professionale ma cordiale. Usa un ritmo calmo e rassicurante. Fai pause naturali tra le frasi. Mostra empatia quando il cliente esprime frustrazione. Mantieni un tono positivo e propositivo nella risoluzione dei problemi.'
  },
  {
    id: 'reception',
    label: 'Reception / Centralino',
    text: 'Parla con tono accogliente e professionale, come una receptionist esperta. Sii chiara e concisa nelle risposte. Usa un ritmo moderato, non troppo veloce. Mantieni sempre un atteggiamento cortese e disponibile. Saluta sempre con calore.'
  },
  {
    id: 'vendita',
    label: 'Vendita / Commerciale',
    text: 'Parla con entusiasmo controllato e professionalità. Usa un tono convincente ma non aggressivo. Fai pause strategiche per dare peso alle informazioni importanti. Sii energico ma naturale. Adatta il ritmo alla conversazione: più lento per spiegazioni tecniche, più dinamico per i vantaggi.'
  },
  {
    id: 'wake_up',
    label: 'Wake-up / Promemoria',
    text: 'Parla con tono gentile e allegro, come per dare il buongiorno. Sii breve e diretto nel messaggio. Usa un ritmo rilassato ma chiaro. Concludi sempre con un augurio positivo per la giornata.'
  },
  {
    id: 'medico',
    label: 'Studio Medico',
    text: 'Parla con tono calmo, rassicurante e professionale. Usa un ritmo lento e chiaro per garantire la comprensione. Mostra pazienza e empatia. Sii preciso nelle informazioni su appuntamenti e orari. Mantieni sempre un atteggiamento sereno e competente.'
  },
  {
    id: 'naturale_it',
    label: 'Naturale Italiano',
    text: 'Parla in modo naturale e fluido in italiano. Usa intonazione variata come in una conversazione reale. Inserisci micro-pause naturali. Evita di suonare robotico o monotono. Adatta il tono emotivo al contesto della conversazione.'
  }
];

const AGENT_LANGUAGES = [
  { id: 'it', name: 'Italiano', flag: '🇮🇹' },
  { id: 'en', name: 'English', flag: '🇬🇧' },
  { id: 'fr', name: 'Français', flag: '🇫🇷' },
  { id: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { id: 'es', name: 'Español', flag: '🇪🇸' },
];

const STEPS = [
  { label: 'Generale', icon: Bot },
  { label: 'Voce', icon: Volume2 },
  { label: 'Prompt', icon: MessageSquare },
  { label: 'Knowledge', icon: FileText },
  { label: 'Hooks', icon: Zap },
  { label: 'Handoff', icon: Phone },
  { label: 'Avanzate', icon: SlidersHorizontal },
];

export default function OpenAIVoiceAgentModal({
  open,
  onOpenChange,
  agent,
  onSaved,
  activeProvider,
  extensions,
  ringGroups,
}: OpenAIVoiceAgentModalProps) {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const [editingAgent, setEditingAgent] = useState<Partial<AIVoiceAgent>>({});
  const [showFlowBuilder, setShowFlowBuilder] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState({
    voice: 'marin',
    speed: 1.0,
    voice_instructions: '',
    stability: 0.5,
    speaking_rate: 0.9,
    expressiveness: 'conversational',
    handoff_type: '',
    handoff_destination: '',
    vad_type: 'server_vad' as 'server_vad' | 'semantic_vad' | 'none',
    vad_threshold: 0.5,
    vad_prefix_padding_ms: 300,
    vad_silence_duration_ms: 500,
    vad_eagerness: 'auto' as 'low' | 'medium' | 'high' | 'auto',
    aec_enabled: false,
  });

  const [isTestingVoice, setIsTestingVoice] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  // Models loaded via centralized hook (Task #1066). Lift-and-shift only:
  // identical shape to previous local fetch, zero behavior change.
  const llmModelsQuery = useChatModelOptions('openai-realtime', { enabled: open });
  const availableModels = useMemo(() => {
    const list = llmModelsQuery.data?.models;
    if (!list || list.length === 0) return FALLBACK_MODELS;
    const mapped = list
      .filter((m) => m.status !== 'deprecated')
      .map((m) => ({
        id: m.id,
        name: `${m.displayName || m.name || m.id}${m.isRecommended ? ' (Consigliato)' : ''}`,
        description: m.description || '',
      }));
    return mapped.length > 0 ? mapped : FALLBACK_MODELS;
  }, [llmModelsQuery.data]);
  const modelsOffline = llmModelsQuery.data?.source === 'fallback' || llmModelsQuery.isError;

  const isEditing = !!agent;

  // Task #1316 — guard cambio chat model con doc multimodali Gemini.
  const kbGuard = useKbModelChangeGuard();

  const defaultVoiceSettings = useMemo(() => ({
    voice: 'marin',
    speed: 1.0,
    voice_instructions: '',
    stability: 0.5,
    speaking_rate: 0.9,
    expressiveness: 'conversational',
    handoff_type: '',
    handoff_destination: '',
    vad_type: 'server_vad' as 'server_vad' | 'semantic_vad' | 'none',
    vad_threshold: 0.5,
    vad_prefix_padding_ms: 300,
    vad_silence_duration_ms: 500,
    vad_eagerness: 'auto' as 'low' | 'medium' | 'high' | 'auto',
    aec_enabled: false,
  }), []);

  useEffect(() => {
    if (open) {
      if (agent) {
        const rc = agent.realtime_config || {};
        setEditingAgent({
          name: agent.name,
          description: agent.description || '',
          model: agent.model,
          temperature: agent.temperature,
          language: rc.language || agent.language || 'it',
          max_tokens: agent.max_tokens,
          system_prompt: agent.system_prompt,
          provider_id: agent.provider_id || null,
          rag_enabled: agent.rag_enabled,
          rag_inferential_enabled: agent.rag_inferential_enabled ?? false,
          rag_synthesis_mode: agent.rag_synthesis_mode || 'native',
          available_tools: agent.available_tools || ['transfer_to_human', 'query_knowledge_base', 'end_call'],
          top_p: rc.top_p ?? 1.0,
          frequency_penalty: rc.frequency_penalty ?? 0.0,
          presence_penalty: rc.presence_penalty ?? 0.0,
          // Task #2089 — campi famiglia `gpt-realtime-2`.
          reasoning_effort: rc.reasoning_effort,
          preambles_enabled: rc.preambles_enabled,
          transcription_enabled: agent.transcription_enabled || false,
          sentiment_enabled: agent.sentiment_enabled || false,
          context_memory_enabled: agent.context_memory_enabled || false,
          semantic_memory_enabled: agent.semantic_memory_enabled || false,
          pre_call_hooks: agent.pre_call_hooks,
          flow_definition: agent.flow_definition || null,
        });

        setVoiceSettings({
          voice: rc.voice || agent.voice_id || 'marin',
          speed: rc.speed || 1.0,
          voice_instructions: rc.voice_instructions || '',
          stability: rc.stability || 0.5,
          speaking_rate: rc.speaking_rate || 0.9,
          expressiveness: rc.expressiveness || 'conversational',
          handoff_type: rc.handoff_type || '',
          handoff_destination: rc.handoff_destination || '',
          vad_type: rc.vad_type || 'server_vad',
          vad_threshold: rc.vad_threshold ?? 0.5,
          vad_prefix_padding_ms: rc.vad_prefix_padding_ms ?? 300,
          vad_silence_duration_ms: rc.vad_silence_duration_ms ?? 500,
          vad_eagerness: rc.vad_eagerness || 'auto',
          aec_enabled: rc.aec_enabled ?? false,
        });
      } else {
        setEditingAgent({
          name: '',
          description: '',
          model: 'gpt-realtime-2',
          temperature: 0.7,
          language: 'it',
          max_tokens: 4096,
          system_prompt: '',
          provider_id: null,
          rag_enabled: false,
          rag_inferential_enabled: false,
          rag_synthesis_mode: 'native',
          available_tools: ['transfer_to_human', 'query_knowledge_base', 'end_call'],
          top_p: 1.0,
          frequency_penalty: 0.0,
          presence_penalty: 0.0,
          // Task #2089 — default famiglia `gpt-realtime-2`: reasoning low
          // (best trade-off latenza/qualità per voice agents), preambles
          // attivi (conferma vocale prima dei tool calls lunghi).
          reasoning_effort: 'low',
          preambles_enabled: true,
          transcription_enabled: false,
          sentiment_enabled: false,
          context_memory_enabled: false,
          semantic_memory_enabled: false,
        });
        setVoiceSettings({ ...defaultVoiceSettings });
      }

      setCurrentStep(0);
      setAudioUrl(null);
    }
  }, [open, agent, defaultVoiceSettings]);

  const saveAgent = async () => {
    if (!editingAgent.name?.trim()) {
      toast({ title: 'Inserisci un nome per l\'agente', variant: 'destructive' });
      return;
    }
    if (!isEditing && !editingAgent.system_prompt?.trim()) {
      toast({ title: 'Inserisci un prompt di sistema', variant: 'destructive' });
      return;
    }
    await kbGuard.runGuardedSave({
      agentId: isEditing ? agent?.id ?? null : null,
      newChatModel: editingAgent.model ?? null,
      performSave: (opts) => doSaveAgent(opts),
    });
  };

  const doSaveAgent = async (opts: KbGuardedPerformSaveArgs): Promise<void> => {
    void opts;
    try {
      setIsProcessing(true);
      if (isEditing) {
        const payload = {
          ...editingAgent,
          voice_id: voiceSettings.voice,
          realtime_config: {
            voice: voiceSettings.voice,
            speed: voiceSettings.speed,
            voice_instructions: voiceSettings.voice_instructions,
            stability: voiceSettings.stability,
            speaking_rate: voiceSettings.speaking_rate,
            expressiveness: voiceSettings.expressiveness,
            handoff_type: voiceSettings.handoff_type,
            handoff_destination: voiceSettings.handoff_destination,
            vad_type: voiceSettings.vad_type,
            vad_threshold: voiceSettings.vad_threshold,
            vad_prefix_padding_ms: voiceSettings.vad_prefix_padding_ms,
            vad_silence_duration_ms: voiceSettings.vad_silence_duration_ms,
            vad_eagerness: voiceSettings.vad_eagerness,
            aec_enabled: voiceSettings.aec_enabled,
            top_p: editingAgent.top_p,
            frequency_penalty: editingAgent.frequency_penalty,
            presence_penalty: editingAgent.presence_penalty,
            // Task #2089 — propaga reasoning_effort + preambles SOLO se il
            // modello selezionato è famiglia `gpt-realtime-2`. I modelli GA
            // legacy ignorano questi campi a livello API.
            ...(isRealtime2Model(editingAgent.model) ? {
              reasoning_effort: (editingAgent as { reasoning_effort?: string }).reasoning_effort,
              preambles_enabled: (editingAgent as { preambles_enabled?: boolean }).preambles_enabled,
            } : {}),
          },
        };
        await apiClient.put(`/ai/voice-agents/${agent.id}`, payload);
        toast({ title: 'Successo', description: 'Configurazione salvata' });
      } else {
        const createRes = await apiClient.post<{ id: string }>('/ai/agents', {
          name: editingAgent.name,
          description: editingAgent.description || '',
          model: editingAgent.model || 'gpt-realtime-2',
          temperature: editingAgent.temperature ?? 0.7,
          max_tokens: editingAgent.max_tokens ?? 4096,
          system_prompt: editingAgent.system_prompt,
          rag_enabled: editingAgent.rag_enabled || false,
          rag_synthesis_mode: editingAgent.rag_synthesis_mode || 'native',
        });
        const newAgentId = createRes?.data?.id;
        if (!newAgentId) {
          toast({ title: 'Attenzione', description: 'Agente creato ma impossibile configurare voce/lingua. Apri "Configura" per completare la configurazione.', variant: 'destructive' });
        } else {
          try {
            // Task #2089 — persistiamo già in fase di creazione anche
            // reasoning_effort + preambles_enabled (SOLO se modello famiglia
            // v2). Senza questo PUT i campi scelti dall'utente al primo step
            // andrebbero persi e ricomparirebbero al default solo dopo
            // "Configura" sull'agente esistente.
            await apiClient.put(`/ai/voice-agents/${newAgentId}`, {
              voice_id: voiceSettings.voice,
              language: editingAgent.language || 'it',
              realtime_config: {
                voice: voiceSettings.voice,
                language: editingAgent.language || 'it',
                ...(isRealtime2Model(editingAgent.model) ? {
                  reasoning_effort: (editingAgent as { reasoning_effort?: string }).reasoning_effort,
                  preambles_enabled: (editingAgent as { preambles_enabled?: boolean }).preambles_enabled,
                } : {}),
              },
            });
          } catch {
            toast({ title: 'Attenzione', description: 'Agente creato, ma voce/lingua non configurate. Apri "Configura" per completare.', variant: 'destructive' });
          }
        }
        toast({ title: 'Agente creato', description: `"${editingAgent.name}" è stato creato con successo` });
      }
      onOpenChange(false);
      onSaved();
    } catch (error: unknown) {
      // Task #1435 — limite tenant agenti AI: modal globale già aperto.
      if (isTenantLimitHandled(error)) {
        onOpenChange(false);
        return;
      }
      const errMsg = error instanceof Error ? error.message : 'Errore durante il salvataggio';
      toast({ title: 'Errore', description: errMsg, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const testVoice = async () => {
    if (!agent) return;
    try {
      setIsTestingVoice(true);
      setAudioUrl(null);
      const response = await apiClient.post<{ audioUrl?: string }>(`/ai/voice-agents/${agent.id}/tts-sample`, {
        voice: voiceSettings.voice,
        speed: voiceSettings.speed,
        voice_instructions: voiceSettings.voice_instructions,
        text: 'Ciao! Sono il tuo assistente vocale AI. Come posso aiutarti oggi?',
      });
      // La TTS sample può arrivare sia dentro l'envelope (data.audioUrl) sia al
      // top-level a seconda della route: leggiamo entrambe le forme.
      const responseFlat = response as ApiResponse<{ audioUrl?: string }> & { audioUrl?: string };
      const audioUrlValue = responseFlat.data?.audioUrl || responseFlat.audioUrl;
      if (audioUrlValue) {
        setAudioUrl(audioUrlValue);
        toast({ title: 'Audio generato', description: 'Premi play per ascoltare' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Impossibile generare audio', variant: 'destructive' });
    } finally {
      setIsTestingVoice(false);
    }
  };

  const sections: AgentSectionDef[] = STEPS.map((s, i) => ({
    id: `s${i}`,
    label: s.label,
    icon: <s.icon className="h-4 w-4" />,
  }));
  const activeSectionId = `s${currentStep}`;
  const handleSectionChange = (id: string) => {
    const idx = parseInt(id.replace('s', ''), 10);
    if (!Number.isNaN(idx)) setCurrentStep(idx);
  };

  return (
    <>
    <AgentConfigShell
      open={open && !showFlowBuilder}
      onOpenChange={onOpenChange}
      title={isEditing ? `Configura ${agent?.name || 'Agent'}` : 'Nuovo Agente Vocale OpenAI'}
      subtitle={isEditing ? 'Personalizza modello, prompt e knowledge base RAG' : 'Configura nome, modello, voce e prompt per il nuovo agente'}
      icon={isEditing ? <Settings className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
      accentColor="sky"
      sections={sections}
      activeSectionId={activeSectionId}
      onSectionChange={handleSectionChange}
      onSave={saveAgent}
      footerRight={
        <>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={saveAgent} disabled={isProcessing} size="sm" className={isEditing ? 'bg-sky-500 hover:bg-sky-600' : 'bg-emerald-600 hover:bg-emerald-700'}>
            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? 'Salva' : 'Crea Agente'}
          </Button>
        </>
      }
    >
        <div className="space-y-4">
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Nome Agente</Label>
                  <Input
                    value={editingAgent.name || ''}
                    onChange={(e) => setEditingAgent({ ...editingAgent, name: e.target.value })}
                    placeholder="Agent Voice AI 1"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs font-medium">Modello</Label>
                    {modelsOffline && (
                      <span className="text-[10px] px-1.5 py-0 rounded border border-amber-300 bg-amber-50 text-amber-700" title="La lista modelli proviene dal fallback locale: la chiamata API al provider non è andata a buon fine.">⚠️ Lista offline — verifica chiave API</span>
                    )}
                  </div>
                  <Select
                    value={editingAgent.model || 'gpt-realtime-2'}
                    onValueChange={(v) => setEditingAgent({ ...editingAgent, model: v })}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModels.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Descrizione</Label>
                <Input
                  value={editingAgent.description || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, description: e.target.value })}
                  placeholder="Assistente AI per vendite e supporto..."
                  className="h-9 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Temperature: {editingAgent.temperature || 0.7}</Label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={editingAgent.temperature || 0.7}
                  onChange={(e) => setEditingAgent({ ...editingAgent, temperature: parseFloat(e.target.value) })}
                  className="w-full accent-sky-500"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Preciso (0)</span>
                  <span>Bilanciato (0.7)</span>
                  <span>Creativo (2)</span>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Voce</Label>
                <Select
                  value={voiceSettings.voice}
                  onValueChange={(v) => setVoiceSettings({ ...voiceSettings, voice: v })}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TTS_VOICES.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        <span className="flex items-center gap-2">
                          <span>{v.name}</span>
                          {v.quality === 'premium' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">PREMIUM</span>
                          )}
                          <span className="text-xs text-gray-400">({v.description})</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Lingua Agent</Label>
                <Select
                  value={editingAgent.language || 'it'}
                  onValueChange={(v) => setEditingAgent({ ...editingAgent, language: v })}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.id} value={lang.id}>
                        <span>{lang.flag} {lang.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-medium">Istruzioni Voce</Label>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs">
                          <p className="font-semibold mb-1">Stile vocale dell'agente</p>
                          <p>Definisce COME l'agente parla: tono di voce, emozioni, ritmo, pause e stile espressivo. Non riguarda il contenuto delle risposte.</p>
                          <p className="mt-1 text-text-muted">Es: "Parla con tono caldo, usa pause naturali, mostra empatia nelle risposte."</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-medium">
                    Controlla tono, emozione e ritmo
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {VOICE_INSTRUCTION_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => setVoiceSettings({ ...voiceSettings, voice_instructions: preset.text })}
                      className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                        voiceSettings.voice_instructions === preset.text
                          ? 'border-sky-500 bg-sky-50 dark:bg-sky-500/20 text-sky-600 dark:text-sky-300'
                          : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                  {voiceSettings.voice_instructions && (
                    <button
                      type="button"
                      onClick={() => setVoiceSettings({ ...voiceSettings, voice_instructions: '' })}
                      className="text-xs px-2 py-1 rounded-md border border-red-300 dark:border-red-600/50 bg-red-50 dark:bg-red-900/20 text-red-400 hover:border-red-500"
                    >
                      Cancella
                    </button>
                  )}
                </div>
                <Textarea
                  value={voiceSettings.voice_instructions}
                  onChange={(e) => setVoiceSettings({ ...voiceSettings, voice_instructions: e.target.value })}
                  placeholder="Es: Parla con tono caldo e professionale, usa pause naturali tra le frasi, mostra empatia..."
                  rows={3}
                  className="text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Speaking Rate: {voiceSettings.speaking_rate.toFixed(1)}</Label>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={voiceSettings.speaking_rate}
                    onChange={(e) => setVoiceSettings({ ...voiceSettings, speaking_rate: parseFloat(e.target.value) })}
                    className="w-full accent-sky-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Lento (0.5)</span>
                    <span>Naturale (0.9)</span>
                    <span>Veloce (1.5)</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Stability: {voiceSettings.stability.toFixed(1)}</Label>
                  <input
                    type="range"
                    min="0.0"
                    max="1.0"
                    step="0.1"
                    value={voiceSettings.stability}
                    onChange={(e) => setVoiceSettings({ ...voiceSettings, stability: parseFloat(e.target.value) })}
                    className="w-full accent-purple-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Variabile (0)</span>
                    <span>Bilanciato (0.5)</span>
                    <span>Stabile (1)</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Stile Espressivo</Label>
                <Select
                  value={voiceSettings.expressiveness}
                  onValueChange={(v) => setVoiceSettings({ ...voiceSettings, expressiveness: v })}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conversational">Conversational (Naturale)</SelectItem>
                    <SelectItem value="neutral">Neutral (Formale)</SelectItem>
                    <SelectItem value="expressive">Expressive (Emotivo)</SelectItem>
                    <SelectItem value="calm">Calm (Tranquillo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {activeProvider?.routing_mode === 'openai_sip' && (
                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/30">
                  <p className="text-xs text-purple-600 dark:text-purple-300">
                    <strong>Modalità SIP Diretto:</strong> Speaking Rate, Stability e Stile Espressivo vengono inclusi nelle istruzioni vocali inviate a OpenAI per guidare il comportamento del modello.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button
                  onClick={testVoice}
                  disabled={isTestingVoice}
                  variant="outline"
                  size="sm"
                  className="border-orange-300 dark:border-orange-500/50 hover:border-orange-500 hover:bg-orange-500/10"
                >
                  {isTestingVoice ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Play className="h-4 w-4 mr-2 text-orange-400" />
                  )}
                  Testa Voce
                </Button>
                {audioUrl && (
                  <audio controls src={audioUrl} className="h-8 flex-1">
                    Il tuo browser non supporta l'elemento audio.
                  </audio>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs font-medium">System Prompt</Label>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs text-xs">
                        <p className="font-semibold mb-1">Prompt principale dell'agente</p>
                        <p>Definisce COSA fa l'agente: personalità, compiti, regole di comportamento, informazioni sull'azienda e istruzioni operative.</p>
                        <p className="mt-1 text-text-muted">Es: "Sei l'assistente di Studio Rossi. Rispondi alle domande su orari, servizi e prenota appuntamenti."</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
                <Textarea
                  value={editingAgent.system_prompt || ''}
                  onChange={(e) => setEditingAgent({ ...editingAgent, system_prompt: e.target.value })}
                  rows={8}
                  placeholder="Sei un assistente professionale..."
                  className="text-sm"
                />
                <p className="text-xs text-gray-400">
                  Il prompt principale che definisce identità, compiti e regole dell'agente. Viene usato in tutte le modalità (SIP, WebSocket, Chat).
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setShowFlowBuilder(true)}
                >
                  <GitBranch className="h-4 w-4 mr-1" />
                  Flow Builder
                </Button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700/50">
                <div>
                  <Label>Knowledge Base RAG</Label>
                  <p className="text-xs text-gray-400">Risposte basate sui tuoi documenti</p>
                </div>
                <Switch
                  checked={editingAgent.rag_enabled || false}
                  onCheckedChange={(checked) => setEditingAgent({ ...editingAgent, rag_enabled: checked })}
                />
              </div>

              {editingAgent.rag_enabled && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700/50 ml-4 border-l-2 border-blue-300">
                  <div className="flex items-center gap-1.5">
                    <Label>Sintesi Nativa</Label>
                    <div className="relative group">
                      <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-64 p-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                        <p className="font-medium mb-1">ON: L'agente ragiona direttamente sui dati recuperati dalla Knowledge Base. Più veloce, più naturale, costo inferiore.</p>
                        <p>OFF: I dati vengono prima sintetizzati da un modello ausiliario (GPT-4o-mini) e poi passati all'agente. Più controllata, utile con modelli costosi.</p>
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={(editingAgent.rag_synthesis_mode || 'native') === 'native'}
                    onCheckedChange={(checked) => setEditingAgent({ ...editingAgent, rag_synthesis_mode: checked ? 'native' : 'pre_synthesized', rag_inferential_enabled: !checked })}
                  />
                </div>
              )}

              {editingAgent.rag_enabled && (
                isEditing && agent?.id ? (
                  <KnowledgeBaseManager agentId={agent.id} agentType="openai_voice" />
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Salva prima l'agente per gestire la Knowledge Base</p>
                  </div>
                )
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <PreCallHooksSection
                hooks={editingAgent.pre_call_hooks || { inject_caller_number: true, inject_datetime: true, auto_crm_lookup: true }}
                onChange={(hooks) => setEditingAgent({ ...editingAgent, pre_call_hooks: hooks })}
                agentType="voice"
              />
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-4">
              <div className="space-y-3">
                <Label className="text-xs font-medium">Handoff Operatore</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-400">Tipo Destinazione</Label>
                    <Select
                      value={voiceSettings.handoff_type || '__none__'}
                      onValueChange={(value) => {
                        setVoiceSettings({
                          ...voiceSettings,
                          handoff_type: value === '__none__' ? '' : value,
                          handoff_destination: '',
                        });
                      }}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Seleziona tipo..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          <span className="text-gray-400">Nessuno</span>
                        </SelectItem>
                        <SelectItem value="extension">Estensione</SelectItem>
                        <SelectItem value="ring_group">Ring Group</SelectItem>
                        <SelectItem value="queue">Coda</SelectItem>
                        <SelectItem value="ivr">IVR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {voiceSettings.handoff_type && voiceSettings.handoff_type !== '' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-400">Destinazione</Label>
                      <Select
                        value={voiceSettings.handoff_destination || '__none__'}
                        onValueChange={(value) => setVoiceSettings({
                          ...voiceSettings,
                          handoff_destination: value === '__none__' ? '' : value,
                        })}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            <span className="text-gray-400">Seleziona...</span>
                          </SelectItem>
                          {voiceSettings.handoff_type === 'extension' && extensions?.map((ext) => (
                            <SelectItem key={ext.id} value={ext.extension_number}>
                              {ext.extension_number} - {ext.display_name}
                            </SelectItem>
                          ))}
                          {voiceSettings.handoff_type === 'ring_group' && ringGroups?.map((rg) => (
                            <SelectItem key={rg.id} value={rg.extension}>
                              {rg.extension} - {rg.name}
                            </SelectItem>
                          ))}
                          {voiceSettings.handoff_type === 'queue' && (
                            <SelectItem value="queue_main">Coda Principale</SelectItem>
                          )}
                          {voiceSettings.handoff_type === 'ivr' && (
                            <SelectItem value="ivr_main">IVR Principale</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400">
                  Destinazione a cui trasferire quando l'utente richiede un operatore umano
                </p>
              </div>
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-4">
              {/* Task #2089 — Reasoning + Preambles: solo famiglia gpt-realtime-2 */}
              {isRealtime2Model(editingAgent.model) && (
                <Card className="border-sky-200 dark:border-sky-700/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Bot className="h-4 w-4 text-sky-500" />
                      Reasoning &amp; Preambles
                      <span className="text-[10px] px-1.5 py-0 rounded border border-sky-300 bg-sky-50 text-sky-700">GPT Realtime 2</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">Reasoning Effort</Label>
                        <Select
                          value={(editingAgent as { reasoning_effort?: string }).reasoning_effort || 'low'}
                          onValueChange={(v) => setEditingAgent({ ...editingAgent, reasoning_effort: v as 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' })}
                        >
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minimal">Minimal — latenza più bassa</SelectItem>
                            <SelectItem value="low">Low (default voice agents)</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High — risposte più ragionate</SelectItem>
                            <SelectItem value="xhigh">XHigh — massimo ragionamento</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Controlla quanto il modello "pensa" prima di rispondere. Higher = più qualità, più latenza e costo.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-medium">Preambles (conferme vocali)</Label>
                          <Switch
                            checked={(editingAgent as { preambles_enabled?: boolean }).preambles_enabled ?? true}
                            onCheckedChange={(v) => setEditingAgent({ ...editingAgent, preambles_enabled: v })}
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Il modello emette una breve conferma vocale ("un attimo, controllo...") prima di eseguire tool calls lunghi.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeProvider?.routing_mode !== 'openai_sip' && (
                <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700/50 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                    <Zap className="h-4 w-4 text-yellow-400" />
                    Parametri Avanzati OpenAI
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(Solo WebSocket/Dual Stack)</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Max Tokens: {editingAgent.max_tokens || 4096}</Label>
                      <input
                        type="range"
                        min="256"
                        max="16384"
                        step="256"
                        value={editingAgent.max_tokens || 4096}
                        onChange={(e) => setEditingAgent({ ...editingAgent, max_tokens: parseInt(e.target.value) })}
                        className="w-full accent-green-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>256</span>
                        <span>4096</span>
                        <span>16384</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Top P: {editingAgent.top_p?.toFixed(2) || '1.00'}</Label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={editingAgent.top_p || 1.0}
                        onChange={(e) => setEditingAgent({ ...editingAgent, top_p: parseFloat(e.target.value) })}
                        className="w-full accent-purple-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Focusato (0)</span>
                        <span>Default (1)</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Frequency Penalty: {editingAgent.frequency_penalty?.toFixed(1) || '0.0'}</Label>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={editingAgent.frequency_penalty || 0}
                        onChange={(e) => setEditingAgent({ ...editingAgent, frequency_penalty: parseFloat(e.target.value) })}
                        className="w-full accent-orange-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Nessuna (0)</span>
                        <span>Alta (2)</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Penalizza parole ripetute</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Presence Penalty: {editingAgent.presence_penalty?.toFixed(1) || '0.0'}</Label>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={editingAgent.presence_penalty || 0}
                        onChange={(e) => setEditingAgent({ ...editingAgent, presence_penalty: parseFloat(e.target.value) })}
                        className="w-full accent-pink-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Nessuna (0)</span>
                        <span>Alta (2)</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Favorisce nuovi argomenti</p>
                    </div>
                  </div>
                </div>
              )}

              <Card className="border-gray-200 dark:border-gray-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Mic className="h-4 w-4 text-green-400" />
                    Rilevamento Voce (VAD) & Echo Cancellation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Tipo VAD</Label>
                      <Select
                        value={voiceSettings.vad_type}
                        onValueChange={(v) => setVoiceSettings({ ...voiceSettings, vad_type: v as 'server_vad' | 'semantic_vad' | 'none' })}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="server_vad">Server VAD (Classico)</SelectItem>
                          <SelectItem value="semantic_vad">Semantic VAD (Consigliato)</SelectItem>
                          <SelectItem value="none">Disabilitato (Push-to-talk)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {voiceSettings.vad_type === 'semantic_vad'
                          ? 'Semantic VAD capisce quando l\'utente ha finito di parlare in modo intelligente, riducendo le interruzioni'
                          : voiceSettings.vad_type === 'server_vad'
                          ? 'Server VAD rileva automaticamente quando l\'utente parla basandosi sul silenzio'
                          : 'Rilevamento vocale disabilitato'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium">Riduzione Rumore / Echo (AEC)</Label>
                        <Switch
                          checked={voiceSettings.aec_enabled}
                          onCheckedChange={(v) => setVoiceSettings({ ...voiceSettings, aec_enabled: v })}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Attiva filtro far-field di OpenAI. Riduce eco e rumori su linee PSTN/SIP</p>
                    </div>
                  </div>

                  {voiceSettings.vad_type === 'server_vad' && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">Soglia Attivazione (Threshold): {voiceSettings.vad_threshold.toFixed(2)}</Label>
                        <input
                          type="range"
                          min="0.0"
                          max="1.0"
                          step="0.05"
                          value={voiceSettings.vad_threshold}
                          onChange={(e) => setVoiceSettings({ ...voiceSettings, vad_threshold: parseFloat(e.target.value) })}
                          className="w-full accent-green-500"
                        />
                        <div className="flex justify-between text-xs text-gray-400">
                          <span>Sensibile (0.0)</span>
                          <span>Default (0.5)</span>
                          <span>Robusto (1.0)</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs font-medium">Prefix Padding: {voiceSettings.vad_prefix_padding_ms} ms</Label>
                          <input
                            type="range"
                            min="100"
                            max="500"
                            step="50"
                            value={voiceSettings.vad_prefix_padding_ms}
                            onChange={(e) => setVoiceSettings({ ...voiceSettings, vad_prefix_padding_ms: parseInt(e.target.value) })}
                            className="w-full accent-green-500"
                          />
                          <div className="flex justify-between text-xs text-gray-400">
                            <span>100ms</span>
                            <span>300ms</span>
                            <span>500ms</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs font-medium">Durata Silenzio: {voiceSettings.vad_silence_duration_ms} ms</Label>
                          <input
                            type="range"
                            min="200"
                            max="1500"
                            step="100"
                            value={voiceSettings.vad_silence_duration_ms}
                            onChange={(e) => setVoiceSettings({ ...voiceSettings, vad_silence_duration_ms: parseInt(e.target.value) })}
                            className="w-full accent-green-500"
                          />
                          <div className="flex justify-between text-xs text-gray-400">
                            <span>200ms</span>
                            <span>500ms</span>
                            <span>1500ms</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {voiceSettings.vad_type === 'semantic_vad' && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">Reattività (Eagerness)</Label>
                        <Select
                          value={voiceSettings.vad_eagerness || 'auto'}
                          onValueChange={(v) => setVoiceSettings({ ...voiceSettings, vad_eagerness: v as 'low' | 'medium' | 'high' | 'auto' })}
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Bassa (attende fino a 8s)</SelectItem>
                            <SelectItem value="medium">Media (attende fino a 4s)</SelectItem>
                            <SelectItem value="high">Alta (attende fino a 2s)</SelectItem>
                            <SelectItem value="auto">Automatica</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30">
                        <p className="text-xs text-blue-600 dark:text-blue-300">
                          <strong>Semantic VAD</strong> usa un modello AI per capire semanticamente quando l'utente ha finito di parlare, invece di basarsi solo sul silenzio. Risultato: meno interruzioni e conversazioni più naturali.
                        </p>
                      </div>
                    </>
                  )}

                  {voiceSettings.vad_type === 'none' && (
                    <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30">
                      <p className="text-xs text-yellow-600 dark:text-yellow-300">
                        <strong>Push-to-talk:</strong> Il rilevamento vocale automatico è disabilitato. L'utente deve segnalare manualmente quando ha finito di parlare.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Label className="text-base font-semibold text-gray-900 dark:text-white">Funzionalità Avanzate</Label>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Trascrizione Chiamate</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Salva il testo di ogni conversazione</p>
                    </div>
                    <Switch
                      checked={editingAgent.transcription_enabled || false}
                      onCheckedChange={(checked) => setEditingAgent({ ...editingAgent, transcription_enabled: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Analisi Sentiment</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Analizza emozioni durante la chiamata</p>
                    </div>
                    <Switch
                      checked={editingAgent.sentiment_enabled || false}
                      onCheckedChange={(checked) => setEditingAgent({ ...editingAgent, sentiment_enabled: checked })}
                      disabled={!editingAgent.transcription_enabled}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Memoria Contesto</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Ricorda informazioni durante la sessione corrente</p>
                    </div>
                    <Switch
                      checked={editingAgent.context_memory_enabled || false}
                      onCheckedChange={(checked) => setEditingAgent({ ...editingAgent, context_memory_enabled: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Memoria Semantica</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Ricorda fatti e preferenze tra sessioni diverse</p>
                    </div>
                    <Switch
                      checked={editingAgent.semantic_memory_enabled || false}
                      onCheckedChange={(checked) => setEditingAgent({ ...editingAgent, semantic_memory_enabled: checked })}
                    />
                  </div>
                </div>

                {editingAgent.sentiment_enabled && !editingAgent.transcription_enabled && (
                  <p className="text-xs text-amber-600">L'analisi sentiment richiede la trascrizione attiva</p>
                )}
              </div>
            </div>
          )}
        </div>
    </AgentConfigShell>

    <KbModelChangeGuardDialog state={kbGuard.state} />

    <VoiceAgentFlowBuilder
      isOpen={showFlowBuilder}
      onClose={() => setShowFlowBuilder(false)}
      agentType="openai"
      currentPrompt={editingAgent.system_prompt || ''}
      flowDefinition={editingAgent.flow_definition || null}
      agentName={editingAgent.name}
      agentId={editingAgent.id}
      onSave={(prompt, flowDef) => {
        setEditingAgent({ ...editingAgent, system_prompt: prompt, flow_definition: flowDef });
        setShowFlowBuilder(false);
      }}
    />
    </>
  );
}
